import React, { useCallback, useRef } from 'react';
import EDList from '../../../../../../BaseComponents/List/EDList';
import BusinessDataListItem from './BusinessDataListItem';
import { EmptyReason } from '../EmptyReason/EmptyReason';
import { IExtendedBusinessDataProps } from '../BusinessDataSet/BusinessDataSet';
import ErrorBoundary from '../../../../../../Components/ErrorBoundary/ErrorBoundary';
import { makeStyles } from 'tss-react/mui';
import EmptyFilteredComponent from '../EmptyFilteredComponent/EmptyFilteredComponent';
import { useIsSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { FLOATING_MENU_CLEARANCE } from '../../../../../../Components/FloatingBottomArea/FloatingMenu';
import useRegisterScrollTarget from '../../../../../../Components/AppNavbar/useRegisterScrollTarget';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';

const useStyles = makeStyles()(
  theme =>
    ({
      listContainer: {
        padding: 0,
      },
    } as const)
);

/**
 * Komponent som visar en grid med businessdata-kort.
 */
export const BusinessDataList: React.FunctionComponent<
  React.PropsWithChildren<IExtendedBusinessDataProps>
> = props => {
  const { classes } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const miniList = useIsSmallScreen();
  const numberOfNonHeaderColumns = props.columns.filter(
    col => !col.header
  ).length;

  const disableDivider = useCallback(
    (index: number) => index === props.sortedData.rows.length - 1,
    [props.sortedData.rows.length]
  );

  const item = useCallback(
    (index: number) => {
      if (index >= props.sortedData.rows.length) {
        return <div style={{ height: FLOATING_MENU_CLEARANCE }} />;
      }
      const item = props.sortedData.rows[index];
      const nextId = props.sortedData.rows[index + 1]?.id ?? null;
      const prevId = props.sortedData.rows[index - 1]?.id ?? null;
      return (
        item && (
          <BusinessDataListItem
            key={item.id}
            item={item}
            nextId={nextId}
            prevId={prevId}
            columns={props.columns}
            urval={props.urval}
            selectDetail={props.selectDetail}
            selected={
              props.selected.includes(item.id) || props.selectedId === item.id
            }
            toggleSelected={props.toggleSelected}
            showCheckbox={props.showCheckboxes}
            inCard={props.inCard}
            useFunktionsMenyItems={props.useFunktionsMenyItems}
            hideDefaultFunktionsMenyItems={props.hideDefaultFunktionsMenyItems}
            collection={props.collection}
            associationName={props.association?.AssociationName}
            businessIcons={props.businessIcons}
            numberOfColumns={numberOfNonHeaderColumns}
            disabled={props.disabled}
            disableDivider={disableDivider(index)}
            colors={props.colors}
            doNotUseFunktionsMeny={props.doNotUseFunktionsMeny}
            isNew={Boolean(
              props.newItems?.find(newItem => newItem.id === item.id)
            )}
            onDeleteItem={props.onDeleteItem}
            miniList={miniList}
          />
        )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.sortedData.rows,
      props.columns,
      props.urval,
      props.selectDetail,
      props.selected.includes,
      props.selectedId,
      props.toggleSelected,
      props.showCheckboxes,
      props.inCard,
      props.useFunktionsMenyItems,
      props.collection,
      props.businessIcons,
      props.disabled,
      props.colors,
      props.doNotUseFunktionsMeny,
      props.newItems?.find,
      props.onDeleteItem,
      numberOfNonHeaderColumns,
      disableDivider,
    ]
  );
  useRegisterScrollTarget({
    containerRef,
    selector: '[data-virtuoso-scroller="true"]',
    enabled: !props.selectedId,
  });

  const totalCount =
    props.sortedData.rows.length + (props.floatingMenuBottomSpacing ? 1 : 0);
  return (
    <ErrorBoundary name="BusinessDataList">
      <EDBox ref={containerRef} sx={{ height: '100%', width: '100%' }}>
        <EDList
          virtual={!props.inCard}
          totalCount={totalCount}
          item={item}
          className={classes.listContainer}
          noPadding
          inCard={props.inCard}
        >
          {totalCount > 0 ? (
            props.sortedData.rows.map(
              (item, index) =>
                item && (
                  <BusinessDataListItem
                    key={item.id}
                    item={item}
                    prevId={props.sortedData.rows.at(index - 1)?.id ?? null}
                    nextId={props.sortedData.rows.at(index + 1)?.id ?? null}
                    columns={props.columns}
                    urval={props.urval}
                    selectDetail={props.selectDetail}
                    selected={
                      props.selected.includes(item.id) ||
                      props.selectedId === item.id
                    }
                    toggleSelected={props.toggleSelected}
                    showCheckbox={props.showCheckboxes}
                    inCard={props.inCard}
                    useFunktionsMenyItems={props.useFunktionsMenyItems}
                    hideDefaultFunktionsMenyItems={
                      props.hideDefaultFunktionsMenyItems
                    }
                    collection={props.collection}
                    associationName={props.association?.AssociationName}
                    businessIcons={props.businessIcons}
                    numberOfColumns={numberOfNonHeaderColumns}
                    disabled={props.disabled}
                    disableDivider={disableDivider(index)}
                    colors={props.colors}
                    doNotUseFunktionsMeny={props.doNotUseFunktionsMeny}
                    isNew={Boolean(
                      props.newItems?.find(newItem => newItem.id === item.id)
                    )}
                    onDeleteItem={props.onDeleteItem}
                    miniList={miniList}
                  />
                )
            )
          ) : props.sortedData.filtered && !props.loading ? (
            <EmptyFilteredComponent />
          ) : (
            <EmptyReason
              urval={props.urval}
              setUrval={props.setUrval}
              collection={props.collection}
              refetch={props.refetch}
              hasData={props.sortedData.rows.length > 0}
              loading={props.loading}
              inCard={props.inCard}
            />
          )}
        </EDList>
      </EDBox>
    </ErrorBoundary>
  );
};

export default BusinessDataList;
